import React, {useEffect, useState} from 'react'
import Chart from "react-apexcharts";
import {IBudgetCalc, IFutureYear} from "../../../../components/calculator/Calculator";
import {globalColors} from "../../../../styles/globalStyles"
import {compareDesc, isToday, startOfTomorrow} from "date-fns";
import cloneDeep from "lodash.clonedeep";
import {dateToLocalFormat, getISODateFromDate, numberToMoneyFormat} from "../../../../stores/StoreUtilities";
import {Paper, TableContainer, useTheme} from "@mui/material";
import Model from "../../../../model/Model";
import Snapshot, {ISnapshotDetail} from "../../../../model/Snapshot";
import {
  SnapshotType,
  UpdateDeductionInput, UpdateExpenseInput,
  UpdateIncomeInput,
  UpdateTaxInput
} from "../../../../API";
import {useResources} from "../../../../stores/ResourceProvider";
import BudgetHistoryTable from "./BudgetHistoryTable";
import BudgetSnapshotDialog from "../BudgetSnapshotDialog";
import Income from "../../../../model/Income";
import Deduction from "../../../../model/Deduction";
import Tax, {TaxType} from "../../../../model/Tax";
import Expense from "../../../../model/Expense";
import IModelItem from "../../../../model/IModelItem";
import Schedule, {Frequency} from "../../../../model/Schedule";
import isEqual from "lodash.isequal";

const defaultOptions = {
  chart: {
    id: "area-datetime",
    type: 'area',
    stacked: true,
    height: 350,
    width: "100%",
    zoom: {
      enabled: false,
      autoScaleYaxis: true
    },
    // sparkline: {
    //   enabled: true
    // },
    events: {
      mounted: (chart: any) => {
        chart.windowResizeHandler();
      }
    },
    toolbar: {
      show: false,
      tools: {
        download: false
      }
    },
  },
  dataLabels: {
    enabled: false
  },
  legend: {
    position: 'top', // TODO: Figure out why this isn't working
    horizontalAlign: 'left',
    onItemClick: {
      toggleDataSeries: false
    }
  },
  markers: {
    size: 5,
    style: 'hollow',
  },
  xaxis: {
    type: 'datetime',
    min: new Date('01 Jan 2020').getTime(),
    tickAmount: 6,
    axisBorder: {
      show: true
    }
  },
  tooltip: {
    x: {
      format: 'dd MMM yyyy'
    },
    y: {
      formatter: (value: number, options: {series: [number[]], seriesIndex: number, dataPointIndex: number, w: number}) => {
        // This fixes the negative offset value for series 1 Deductions
        if (options.seriesIndex === 1) {
          const adjust = options.series[0][options.dataPointIndex]
          return numberToMoneyFormat(value + adjust, 0)
        } else {
          return numberToMoneyFormat(value, 0)
        }
      }
    }
  },
  colors: [globalColors.income, globalColors.deductionExpense, globalColors.taxExpense, globalColors.essentialExpense, globalColors.discretionaryExpense],
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.7,
      opacityTo: 0.9,
      stops: [0, 100]
    }
  },
  stroke: {
    width: 1
  },
  resonsive: [{
    breakpoint: undefined,
    options: {}
  }]
}

const BudgetHistoryChart = ({
  model,
  thisYearCalc,
  incomes,
  deductions,
  taxes,
  expenses,
  housingExpenses,
  loanAndLiabilityExpenses,
  foodAndPersonalCareExpenses,
  insuranceAndMedicalExpenses,
  vehiclesAndTransportationExpenses,
  travelAndEntertainmentExpenses,
  givingAndMiscExpenses,
  selected,
  onSelect,
  onChange
}: {
  model: Model
  thisYearCalc: IFutureYear
  incomes: Income[]
  deductions: Deduction[]
  taxes: Tax[]
  expenses: Expense[]
  housingExpenses: Expense[],
  loanAndLiabilityExpenses: Expense[],
  foodAndPersonalCareExpenses: Expense[],
  insuranceAndMedicalExpenses: Expense[],
  vehiclesAndTransportationExpenses: Expense[],
  travelAndEntertainmentExpenses: Expense[],
  givingAndMiscExpenses: Expense[],
  selected?: Snapshot
  onSelect?(shapshot: Snapshot): void
  onChange?(item: IModelItem): void
}) => {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([])
  const [budgetCalcs, setBudgetCalcs] = useState<IBudgetCalc[]>([])
  const [estimatedTaxes, setEstimatedTaxes] = useState<Tax[]>([])
  const [options, setOptions] = useState<any>()
  const [series, setSeries] = useState<any>()
  const [currentSnapshot, setCurrentSnapshot] = useState<Snapshot | undefined>()
  const [compareSnapshot, setCompareSnapshot] = useState<Snapshot | undefined>()
  const [editSnapshot, setEditSnapshot] = useState<Snapshot | undefined>()

  const {calculator, modelStore} = useResources()
  const theme = useTheme()

  useEffect(() => {
    const snaps = model.snapshots.filter((s: Snapshot) => s.snapshotType === SnapshotType.Budget || s.snapshotType === SnapshotType.Combined)
    setSnapshots(snaps
    )
    let snap: Snapshot | undefined
    if (selected) {
      snap = selected
    } else if (snaps.length > 0) {
      snap = snaps[0]
    }
    setCurrentSnapshot(snap)

    const calcs: IBudgetCalc[] = snaps.map((s: Snapshot) => calculator.calculateBudget(s.date.getFullYear(), model, undefined, s))
    setBudgetCalcs(calcs)

    if (calcs.length > 0) {
      const estimatedTaxesItem = new Tax({
        id: "estimatedFederalTaxes",
        accountId: model.accountId,
        userId: model.userId,
        modelId: model.id,
        model: model,
        taxType: TaxType.FederalIncomeTax,
        description: "Estimated Federal Income Tax",
        amount: 0,
        schedule: new Schedule({
          name: "Annual",
          frequency: Frequency.Yearly,
          interval: 1
        }),
        start: `${(snaps[snaps.length-1]).date.getFullYear()}-01-01`
      })
      setEstimatedTaxes([estimatedTaxesItem])

      // Update budget estimated tax
      if (thisYearCalc.taxEstimate) {
        const totalEstimatedTaxes = thisYearCalc.taxEstimate.totalEstimatedTax
        calcs[0].totalEstimatedTaxes = totalEstimatedTaxes
        const latestSnapshot = snaps[0]
        const schedule = new Schedule({
          name: "Annual",
          frequency: Frequency.Yearly,
          interval: 1
        })
        latestSnapshot.setSnapshotDetail({
          id: "estimatedFederalTaxes",
          typename: "Tax",
          balance: 0,
          amount: totalEstimatedTaxes,
          schedule: schedule
        })
      }

      const newOptions: any = cloneDeep(defaultOptions)

      const minDate = snaps.reduce((accum: Date, item: Snapshot) => compareDesc(accum, item.date) > 0 ? accum : item.date, new Date())
      if (minDate) {
        newOptions.xaxis.min = minDate.getTime()
      }

      if (snap !== undefined) {
        let annotations = {}
        if (snap) {
          annotations = {
            xaxis: [
              {
                x: snap.date.getTime(),
                borderColor: theme.palette.secondary.main, //'#00E396',
                label: {
                  borderColor: theme.palette.secondary.main, //'#00E396',
                  orientation: 'horizontal',
                  text: dateToLocalFormat(snap.date, "MMM d, yyyy"),
                  style: {
                    fontSize: '14px',
                    fontWeight: 600
                  }
                }
              }
            ]
          }
        }
        newOptions["annotations"] = annotations
        newOptions.chart.events["markerClick"] = (event: any, chartContext: any, details: {dataPointIndex: number, seriesIndex: number}) => {
          if (details.dataPointIndex >= 0 && onSelect) {
            onSelect(snaps[details.dataPointIndex])
          }
        }
      }
      setOptions(newOptions)
      const incomeData = calcs.map((item: IBudgetCalc) => [item.date.getTime(), item.totalIncome])
      const deductionData = calcs.map((item: IBudgetCalc) => [item.date.getTime(), (-item.totalIncome -item.totalDeductions)])
      const taxData = calcs.map((item: IBudgetCalc) => [item.date.getTime(), (item.totalEstimatedTaxes > 0 ? -item.totalEstimatedTaxes : -item.totalTaxes)])
      const essentialExpenseData = calcs.map((item: IBudgetCalc) => [item.date.getTime(), (-item.totalEssentialExpenses)])
      const discretionaryExpenseData = calcs.map((item: IBudgetCalc) => [item.date.getTime(), (-item.totalDiscretionaryExpenses)])

      if (calcs.length === 1) {
        // Add tomorrow to display graph with one data point
        const time = startOfTomorrow().getTime()
        incomeData.push([time, incomeData[0][1]])
        deductionData.push([time, deductionData[0][1]])
        taxData.push([time, taxData[0][1]])
        essentialExpenseData.push([time, essentialExpenseData[0][1]])
        discretionaryExpenseData.push([time, discretionaryExpenseData[0][1]])
      }

      setSeries([
        {name: "Income", group: "Income", data: incomeData},
        {name: "Deductions & Contributions", group: "Expenses", data: deductionData},
        {name: "Estimated Taxes", group: "Expenses", data: taxData},
        {name: "Essential Expenses", group: "Expenses", data: essentialExpenseData},
        {name: "Discretionary Expenses", group: "Expenses", data: discretionaryExpenseData},
      ])
    }
  }, [model, selected, thisYearCalc])

  const updateBudgetFromSnapshot = async (snapshot: Snapshot) => {
    const accountId = model!.accountId
    const incomePromises: Promise<Income | undefined>[] = []
    const deductionPromises: Promise<Deduction | undefined>[] = []
    const taxPromises: Promise<Tax | undefined>[] = []
    const expensePromises: Promise<Expense| undefined>[] = []
    snapshot.details.forEach((detail: ISnapshotDetail) => {
      if (detail.typename === "Income") {
        const income = incomes.find((i: Income) => i.id === detail.id)
        if (income && ((income.amount !== detail.amount) || !isEqual(income.schedule, detail.schedule))) {
          const update: UpdateIncomeInput = {
            id: detail.id,
            accountId: accountId,
            amount: detail.amount,
            amountDate: getISODateFromDate(snapshot.date),
            schedule: JSON.stringify(detail.schedule)
          }
          incomePromises.push(modelStore.updateIncome(update))
        }
      } else if (detail.typename === "Deduction") {
        const deduction = deductions.find((item: Deduction) => item.id === detail.id)
        if (deduction && ((deduction.amount !== detail.amount) || !isEqual(deduction.schedule, detail.schedule))) {
          const update: UpdateDeductionInput = {
            id: detail.id,
            accountId: accountId,
            amount: detail.amount,
            amountDate: getISODateFromDate(snapshot.date),
            schedule: JSON.stringify(detail.schedule)
          }
          deductionPromises.push(modelStore.updateDeduction(update))
        }
      } else if (detail.typename === "Tax") {
        const tax = taxes.find((item: Tax) => item.id === detail.id)
        if (tax && ((tax.amount !== detail.amount) || !isEqual(tax.schedule, detail.schedule))) {
          const update: UpdateTaxInput = {
            id: detail.id,
            accountId: accountId,
            amount: detail.amount,
            amountDate: getISODateFromDate(snapshot.date),
            schedule: JSON.stringify(detail.schedule)
          }
          taxPromises.push(modelStore.updateTax(update))
        }
      } else if (detail.typename === "Expense") {
        const expense = expenses.find((item: Expense) => item.id === detail.id)
        if (expense && ((expense.amount !== detail.amount) || !isEqual(expense.schedule, detail.schedule))) {
          const update: UpdateExpenseInput = {
            id: detail.id,
            accountId: accountId,
            amount: detail.amount,
            amountDate: getISODateFromDate(snapshot.date),
            schedule: JSON.stringify(detail.schedule)
          }
          expensePromises.push(modelStore.updateExpense(update))
        }
      }
    })
    await Promise.all(incomePromises)
    await Promise.all(deductionPromises)
    await Promise.all(taxPromises)
    await Promise.all(expensePromises)
  }

  const handleNewSnapshot = (snapshot?: Snapshot) => {
    // Copy current snapshot
    const current = currentSnapshot ? currentSnapshot : snapshots[0]
    const newSnapshot = new Snapshot({
      accountId: current.accountId,
      userId: current.userId,
      modelId: current.modelId,
      date: current.date,
      description: "",
      snapshotType: current.snapshotType,
      details: JSON.stringify(current.details)
    })
    setEditSnapshot(newSnapshot)
  }

  const handleSaveSnapshot = async (saveSnapshot: Snapshot) => {
    if (isToday(saveSnapshot.date)) {
      await updateBudgetFromSnapshot(saveSnapshot)
    }
    setEditSnapshot(undefined)
    setCompareSnapshot(undefined)

    if (onChange) {
      onChange(saveSnapshot)
    }
  }

  const handleDeleteSnapshot = async (deleteSnapshot: Snapshot) => {
    setEditSnapshot(undefined)
    // await loadModel(currentModel)
    if (onChange) {
      onChange(deleteSnapshot)
    }
  }

  const renderSnapshotDialog = () => {
    let baseSnap
    let snapshot
    let compare = false

    if (editSnapshot) {
      baseSnap = editSnapshot
      snapshot = editSnapshot
    } else if (currentSnapshot && compareSnapshot) {
      compare = true
      if (currentSnapshot.date.getTime() >= compareSnapshot.date.getTime()) {
        baseSnap = compareSnapshot
        snapshot = currentSnapshot
      } else {
        baseSnap = currentSnapshot
        snapshot = compareSnapshot
      }
    } else {
      return
    }

    let deleteFn
    if (snapshots.length > 1 && !compare) {
      deleteFn = handleDeleteSnapshot
    }

    return (
      <BudgetSnapshotDialog
        baseSnap={baseSnap}
        snapshot={snapshot}
        model={model}
        compare={compare}
        incomes={incomes}
        deductions={deductions}
        taxes={taxes}
        estimatedTaxes={estimatedTaxes}
        expenses={expenses}
        housingExpenses={housingExpenses}
        loanAndLiabilityExpenses={loanAndLiabilityExpenses}
        foodAndPersonalCareExpenses={foodAndPersonalCareExpenses}
        insuranceAndMedicalExpenses={insuranceAndMedicalExpenses}
        vehiclesAndTransportationExpenses={vehiclesAndTransportationExpenses}
        travelAndEntertainmentExpenses={travelAndEntertainmentExpenses}
        givingAndMiscExpenses={givingAndMiscExpenses}
        onCancel={() => {
          setCompareSnapshot(undefined)
          setEditSnapshot(undefined)
        }}
        onSave={handleSaveSnapshot}
        onDelete={deleteFn}
      />
    )
  }

  if (options && series) {
    return (
      <React.Fragment>
        <TableContainer component={Paper} sx={{paddingTop:1, overflowY:"hidden"}}>
          <Chart
            options={options}
            series={series}
            type="area"
            height="500"
            width="100%"
          />
        </TableContainer>
        <BudgetHistoryTable model={model} budgetCalcs={budgetCalcs} snapshots={snapshots} selected={currentSnapshot}
                            onSelect={(snapshot?: Snapshot) => {
                              if (snapshot && onSelect) {
                                onSelect(snapshot)
                              }
                              // setCurrentSnapshot(snapshot)
                            }}
                            onNew={handleNewSnapshot}
                            onEdit={(snapshot: Snapshot) => setEditSnapshot(snapshot)}
                            onCompare={(snapshot: Snapshot) => setCompareSnapshot(snapshot)}
        />
        {(editSnapshot || compareSnapshot) && renderSnapshotDialog()}
      </React.Fragment>
    )
  } else {
    return null
  }
}

export default BudgetHistoryChart